import * as React from "react";
import {
  Checkbox,
  CheckboxVisibility,
  ConstrainMode,
  DetailsHeader,
  DetailsList,
  DetailsListLayoutMode,
  DetailsRow,
  DetailsRowFields,
  Dropdown,
  IColumn,
  Icon,
  IDetailsCheckboxProps,
  IDetailsColumnProps,
  IDetailsColumnStyleProps,
  IDetailsColumnStyles,
  IDetailsHeaderProps,
  IDetailsListStyles,
  IDetailsRowFieldsProps,
  IDetailsRowProps,
  IDropdownStyles,
  IStackTokens,
  IStyleFunctionOrObject,
  Label,
  Overlay,
  Selection,
  Stack,
} from "@fluentui/react";
import { uniqueGUID } from "../../helpers/util";
import { ActionBar, ActionType } from "../Action/ActionBar";
import { Filters } from "../Filters/Filters";
import { FilterLayout } from "../Filters/FilterTypes";
import { InfoBlockProps } from "../InfoBlock/InfoBlock";
import { ProgressDotsIndicator } from "../Progress/ProgressDotsIndicator";
import { Sorted, SortedAscending, SortedDescending } from "../Svg/icons";
import { ListingActionMenu } from "./ListingActionMenu";
import { EmptyListProps, ListingEmptyWatermark } from "./ListingEmptyWatermark";
import { ListingPagination } from "./ListingPagination";
import {
  ActionMenuItem,
  buildListingTestIds,
  FilterOptions,
  GroupingSelect,
  ListingColumn,
  ObjectWithKey,
  SelectionMode,
  SortDirections,
  SortOptions,
  ViewSelect,
  ViewSelectOption,
} from "./ListingTypes";

export interface ListingProps <T> {
  /**
   * Column ID that uniquely identifies each row of the table
   */
  itemKey: string;
  /**
   * List of columns to display in the table
   */
  listColumns: ListingColumn [];
  /**
   * List of items to display in the table
   */
  items: T [];
  /**
   * Number of rows to show per page. If not specified pagination will not be shown
   */
  itemsPerPage?: number;
  /**
   * Specifies if the table shoud support single/ multiple selection or none
   * @default none
   */
  selectionMode?: SelectionMode;
  /**
   * Returns the new list of selected items each time the selection changes
   */
  selectedItems?: (selectedItems: ObjectWithKey[]) => void;
  /**
   * Actions supported by the action bar
   */
  actionBarItems?: ActionType [];
  /**
   * Triggered when the user clicks on any action.
   * Allows executing logic prior to invoking the action.
   */
  onActionClick?: (actionKey: string) => void;
  /**
   * Info block to show under action bar items
   */
  infoBlocks?: InfoBlockProps [];
  /**
   * Allows to disable all actions
   */
  disableAllActions?: boolean;
  /**
   * A placeholder to display when the table is empty
   */
  emptyList?: EmptyListProps;
  /**
   * Sorting proprties
   */
  sorting? : SortOptions;
  /**
   * Indicates if the list should show the shimmer or loading spinner
   */
  isLoading?: boolean;
  /**
   * Select items grouping criteria
   */
  groupingSelect?: GroupingSelect;
  /**
   * Select the view type
   */
  viewSelect?: ViewSelect;
  /**
   * Options to filter items in the list
   */
  filtering?: FilterOptions;
  /**
   * Action menu to manipulate individual rows
   */
  actions?: ActionMenuItem [];
  /**
   * A reference to the listing component
   */
  listingComponentRef?: (listing: ListingComponent) => void;
  /**
   * Minimum width of the listing (defaulted to 800)
   */
  minWidth? :number;
  /**
   * Id used to get the listing elements for unit tests
   */
  testId?: string;
  /**
   * Callback to return whether the row checkbox should be disabled for the item
   */
  isRowSelectionDisabled?: (item: T) => boolean
}

export interface ListingComponent {
  /**
   * A function that removes all selection from the table
   */
  resetTableSelection: () => void;
  /**
   * A function that returns the selected items
   */
  getCurrentSelection: () => ObjectWithKey [];
}

interface Column extends ListingColumn {
  key: string;
  minWidth: number;
  isColumnSorted?: boolean;
  isColumnSortedDescending?: boolean;
  styles?: IStyleFunctionOrObject<IDetailsColumnStyleProps, IDetailsColumnStyles>;
}

interface ColumnMap {
  [key: string]: Column;
}

const groupDropdownStyles: Partial<IDropdownStyles> = {
  root: { width: "200px", height: "24px" },
  title: {
    height: "24px",
    lineHeight: "22px",
    fontSize: "13px",
  },
  caretDownWrapper: { top: "-3px" },
};
const viewDropdownStyles: Partial<IDropdownStyles> = {
  root: { width: "160px", height: "24px" },
  title: {
    height: "24px",
    lineHeight: "22px",
    fontSize: "13px",
  },
  caretDownWrapper: { top: "-3px" },
};
const headerStyles = {
  root: {
    height: "36px",
    paddingTop: "0px",
    marginLeft: "0px",
    ":hover": {
      cursor: "pointer",
      ".ms-DetailsHeader-cellSizer": { "::after": { opacity: 1 } },
    },
  },
  cellSizer: { cursor: "col-resize" },
  cellIsCheck: { width: "26px", left: "-21px" },
};

const cellStyles = {
  root: { height: "32px", ":hover": { backgroundColor: "white" } },
  cellName: { fontSize: "13px" },
  cellTitle: { paddingLeft: "4px" },
};

const tableRowStyles = {
  root: { minHeight: "32px" },
  cell: {
    fontSize: "13px",
    padding: "6px 4px 0px 5px",
    minHeight: "32px",
    color: "rgb(50, 49, 48)",
    a: {
      textDecoration: "none",
      color: "rgb(0, 120, 212)",
      ":hover": { textDecoration: "underline" },
    },
    wordBreak: "break-word",
    whiteSpace: "normal",
  },
  checkCell: { width: "26px", minHeight: "32px", height: "32px" },
};

const headerCheckboxStyle = { root: { width: "0px" } };

const rowCheckboxStyle = {
  root: {
    width: "42px",
    height: "30px",
    margin: "6px",
  },
  checked: {
    width: "42px",
    height: "30px",
    margin: "6px",
  },
};

const rowCheckboxDisabledUncheckedStyle = {
  root: {
    width: "42px",
    height: "30px",
    margin: "6px",
  },
  checked: {
    width: "42px",
    height: "30px",
    margin: "6px",
  },
  checkbox: { background: "#f3f2f1" },
};

const EMPTY_CHAR = "-";
const ACTION_MENU_KEY = "action-menu";
const ACTION_MENU_WIDTH = 10;
const LISTING_CLASS_NAME = "listing";

const renderRowFields = (props: IDetailsRowFieldsProps): JSX.Element => (
  <span data-selection-disabled>
    <DetailsRowFields {...props} />
  </span>
);

function getInitialSorting(columnId: string, columns: Column[]): { columnKey: string, direction?: SortDirections } {
  const initialSorting: { columnKey: string, direction?: SortDirections } = { columnKey: "" };
  columns.forEach(column => {
    if (column.key === columnId) {
      if (!column.initialSortDirection) {
        throw new Error(`column ${column.key} is defined as initial sort column but is not sortable`);
      } else {
        initialSorting.columnKey = column.key;
        initialSorting.direction = column.initialSortDirection ? column.initialSortDirection : undefined;
      }
    }
  });
  return initialSorting;
}

/**
 * A listing that diplays data in a table that can be paginated.
 * It also allows manupulating the data using an optional action bar.
 */

// eslint-disable-next-line react/function-component-definition
export function Listing<T>({
  itemKey,
  listColumns,
  items,
  itemsPerPage,
  selectionMode,
  selectedItems,
  actionBarItems,
  onActionClick,
  infoBlocks,
  disableAllActions,
  emptyList,
  sorting,
  isLoading,
  groupingSelect,
  viewSelect,
  actions,
  filtering,
  listingComponentRef,
  minWidth = 300,
  testId,
  isRowSelectionDisabled,
}: ListingProps<T>): JSX.Element {
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [filteredItems, setFilteredItems] = React.useState<T []>(items);
  const [sortedItems, setSortedItems] = React.useState<T[]>(filteredItems);
  const [verticalContentScrollbarHeight, setVerticalContentScrollbarHeight] = React.useState<number>();
  const [columns, setColumns] = React.useState<Column []>([]);

  const renderHeader = (headerProps?: IDetailsColumnProps): JSX.Element | null => {
    if (!headerProps) {
      return null;
    }
    const column = headerProps?.column as Column;
    let sortIcon;

    if (column.isColumnSorted === false && column.initialSortDirection) {
      return (
        <span data-test-id={column.testId}>
          {column.name}
          {Sorted}
        </span>
      );
    }
    if (column.isColumnSortedDescending === true) {
      sortIcon = SortedDescending;
    } else if (column.isColumnSortedDescending === false) {
      sortIcon = SortedAscending;
    }
    return (
      <span data-test-id={column.testId}>
        {column.name}
        {sortIcon}
      </span>
    );
  };

  const memoColumns = React.useMemo(() => {
    let listColumnsToColumn: Column [] = [];
    if (listColumns) {
      let totalColumnFlexGrow = 0;
      listColumns.forEach(listColumn => {
        totalColumnFlexGrow += listColumn.flexGrow || 1;
      });
      listColumnsToColumn = listColumns.map(listColumn => ({
        key: listColumn.itemProp,
        minWidth: ((minWidth - (actions?.length ? ACTION_MENU_WIDTH : 0)) / totalColumnFlexGrow || 1)
          * (listColumn?.flexGrow || 1),
        onRenderHeader: renderHeader,
        ...listColumn,
        calculatedWidth: 0,
        styles: cellStyles,
      }));

      if (actions) {
        listColumnsToColumn.push({
          key: ACTION_MENU_KEY,
          itemProp: "",
          name: "",
          minWidth: ACTION_MENU_WIDTH,
          // eslint-disable-next-line react/no-unstable-nested-components
          onRenderItems: (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            values: any,
          ) => (
            <span style={{
              display: "block",
              textAlign: "right",
            }}
            >
              <ListingActionMenu
                actions={actions}
                rowValue={values}
                testId={buildListingTestIds(testId).rowActionMenuButton}
              />
            </span>
          ),
        });
      }
    }
    return listColumnsToColumn;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minWidth, JSON.stringify(listColumns)]);

  React.useEffect(() => {
    setColumns(memoColumns);
  }, [memoColumns]);

  React.useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  React.useEffect(() => {
    if (currentSort.current) {
      const { columnKey, direction } = currentSort.current;
      const column = getColumnByKey(columnKey);
      if (column) {
        sortByColumn(column, direction || SortDirections.ASC);
      }
    } else {
      setSortedItems(filteredItems);
    }
    selectionState.setAllSelected(false);
    setCurrentPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filteredItems), JSON.stringify(columns)]);

  const gridStyles: Partial<IDetailsListStyles> = {
    root: { ".ms-List-cell": { minHeight: "32px" } },
    headerWrapper: { flex: "0 0 auto" },
    contentWrapper: {
      flex: "1 1 auto",
      overflowY: "auto",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      height: `${verticalContentScrollbarHeight}px`,
    },
  };

  const [isHeaderCbChecked, setIsHeaderCbChecked] = React.useState<boolean>(false);
  const [isHeaderCbIndeterminate, setIsHeaderCbIndeterminate] = React.useState<boolean>(false);
  const [selectionCount, setSelectionCount] = React.useState<number>(0);
  const [showWatermark, setShowWatermark] = React.useState<boolean>(false);

  const [selectionState] = React.useState<Selection>(new Selection(
    {
      onSelectionChanged: (): void => {
        if (selectedItems) {
          setSelectionCount(selectionState.getSelectedCount());
          return selectedItems(selectionState.getSelection());
        }
        return undefined;
      },
    },
  ));
  const currentSort = React.useRef(
    sorting?.initialSortedColumn && getInitialSorting(sorting.initialSortedColumn || "", memoColumns),
  );

  const columnsMap = columns.reduce((map: ColumnMap, column: Column) => {
    map[column.key || ""] = column;
    return map;
  }, {});
  const viewGroupingStackTokens: IStackTokens = { childrenGap: 10 };
  const pageCount: number = itemsPerPage ? Math.ceil(sortedItems.length / itemsPerPage) : 1;
  const fromItems: number = itemsPerPage ? (
    sortedItems.length ? (currentPage * itemsPerPage
    ) - itemsPerPage + 1 : 0) : 1;
  const totalItems: number = sortedItems.length;
  let toItems: number = itemsPerPage ? currentPage * itemsPerPage : sortedItems.length;
  const itemsToShow: T [] = sortedItems.slice(fromItems - 1, toItems);
  if (itemsPerPage && itemsToShow.length < itemsPerPage) {
    toItems = ((currentPage - 1) * itemsPerPage) + itemsToShow.length;
  }

  const onPrevious = (): void => {
    setCurrentPage(currentPage - 1);
  };

  const onNext = (): void => {
    setCurrentPage(currentPage + 1);
  };

  const onPageSelect = (pageNumber: number): void => {
    setCurrentPage(pageNumber);
  };

  const getSelectionMode = (): SelectionMode | undefined => {
    let mode: SelectionMode | undefined = selectionMode;
    if (selectionMode === undefined) {
      if (!selectedItems) {
        mode = SelectionMode.none;
      } else {
        mode = SelectionMode.multiple;
      }
    }
    return mode;
  };

  const getColumnByKey = (columnKey: string): Column => {
    const column = columns.find(col => columnKey === col.key);
    return column || columns[0];
  };

  const resetTableSelection = (): void => {
    selectionState.setAllSelected(false);
  };

  const getCurrentSelection = (): ObjectWithKey [] => selectionState.getSelection();

  React.useEffect(() => {
    selectionState.setAllSelected(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, currentPage]);

  React.useEffect(() => {
    const tableHeaderHeight = 38;
    const groupingHeight = groupingSelect || viewSelect ? 44 : 0;
    const infoBlocksHeight = infoBlocks ? 32 * infoBlocks.length : 0;
    const filterHeight = filtering ? filtering.filterLayout === FilterLayout.DropDown ? 84 : 43 : 0;
    const paginationControlHeight = (itemsPerPage ? 64 : 0);
    const actionBarHeight = actionBarItems ? 38 : 0;
    const containerHeight = document?.getElementsByClassName(LISTING_CLASS_NAME)[0].clientHeight;
    const safetyBuffer = 2; // to account for extra pixels (probably from Stack) causing a verticla scrollbar to appear
    setVerticalContentScrollbarHeight(
      itemsToShow.length
        ? containerHeight
        - tableHeaderHeight
        - paginationControlHeight
        - actionBarHeight
        - filterHeight
        - infoBlocksHeight
        - groupingHeight
        - safetyBuffer
        : undefined,
    );
  }, [actionBarItems, itemsPerPage, itemsToShow.length, filtering, infoBlocks, groupingSelect, viewSelect]);

  React.useEffect(() => {
    if (selectionCount === 0) {
      setIsHeaderCbChecked(false);
      setIsHeaderCbIndeterminate(false);
    } else if (selectionCount === itemsToShow.length) {
      setIsHeaderCbChecked(true);
      setIsHeaderCbIndeterminate(false);
    } else {
      setIsHeaderCbChecked(false);
      setIsHeaderCbIndeterminate(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionCount]);

  React.useEffect(() => {
    if (itemsToShow.length === 0 && currentPage > 1) {
      onPrevious();
    }
    setShowWatermark(!isLoading && itemsToShow.length === 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, itemsToShow.length]);

  React.useEffect(() => {
    if (listingComponentRef) {
      listingComponentRef({ resetTableSelection, getCurrentSelection });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomColumn = (item: any, _?: number, rendredColumn?: IColumn): JSX.Element => {
    const key: string = rendredColumn?.key || "";
    const fieldContent = item[key];
    const renderElement = columnsMap[key].onRenderItems;
    if (rendredColumn?.key !== ACTION_MENU_KEY) {
      if (fieldContent === undefined || fieldContent === "") {
        return <span>{EMPTY_CHAR}</span>;
      }
    }
    if (renderElement) {
      return renderElement(item);
    }
    return <span>{fieldContent}</span>;
  };

  const [filterKey, setFilterKey] = React.useState(uniqueGUID());

  const copyAndSort = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    itemsToSort: any[],
    columnKey: string,
    isSortedDescending?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    comparator?: (a: any, b: any) => 0 | -1 | 1,
  ): T[] => {
    const key = columnKey;
    return itemsToSort.slice(0).sort((a, b) => {
      if (comparator) {
        return isSortedDescending ? -1 * comparator(a, b) : comparator(a, b);
      }
      if (a[key] === undefined || a[key] === null) {
        return isSortedDescending ? 1 : -1;
      }
      if (b[key] === undefined || b[key] === null) {
        return isSortedDescending ? -1 : 1;
      }
      const aVal = typeof (a[key]) === "string" ? a[key].toLowerCase() : a[key];
      const bVal = typeof (b[key]) === "string" ? b[key].toLowerCase() : b[key];
      if (typeof (aVal) === "string" && typeof (bVal) === "string" && sorting?.locale) {
        const comparaison = isSortedDescending
          ? bVal.localeCompare(aVal, sorting.locale) : aVal.localeCompare(bVal, sorting.locale);
        return comparaison > 0 ? 1 : comparaison < 0 ? -1 : 0;
      }
      return ((isSortedDescending ? aVal > bVal : aVal < bVal) ? -1 : 1);
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortByColumn = (column: Column, direction?: SortDirections): void => {
    let { isColumnSortedDescending } = column;
    if (direction === SortDirections.ASC) {
      isColumnSortedDescending = false;
    } else if (direction === SortDirections.DESC) {
      isColumnSortedDescending = true;
    }
    if (column.isColumnSorted && !direction) {
      isColumnSortedDescending = !isColumnSortedDescending;
    }
    currentSort.current = {
      columnKey: column.key,
      direction: isColumnSortedDescending ? SortDirections.DESC : SortDirections.ASC,
    };
    setSortedItems(copyAndSort(filteredItems, column.key || "", isColumnSortedDescending, column.comparator));
    setColumns(columns.map(col => {
      col.isColumnSorted = col.key === column.key;
      if (col.isColumnSorted) {
        col.isColumnSortedDescending = isColumnSortedDescending;
      }
      return col;
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headerColumnClick = (_?: React.MouseEvent<HTMLElement>, column?: any): void => {
    if (column && columnsMap[column.key].initialSortDirection) {
      selectionState.setAllSelected(false);
      if (column.isColumnSorted) {
        sortByColumn(column);
      } else {
        sortByColumn(column, columnsMap[column.key].initialSortDirection);
      }
    }
  };

  const renderCheckbox = (props: IDetailsCheckboxProps | undefined, isDisabled: boolean): JSX.Element => (
    <div style={{ pointerEvents: "none" }}>
      <Checkbox
        inputProps={
          { "data-test-id": buildListingTestIds(testId).rowCheckbox } as Record<string, string | undefined>
        }
        checked={props?.checked}
        styles={isDisabled && !props?.checked ? rowCheckboxDisabledUncheckedStyle : rowCheckboxStyle}
        disabled={isDisabled}
      />
    </div>
  );

  const renderEnabledCheckbox = (props: IDetailsCheckboxProps | undefined): JSX.Element => (
    renderCheckbox(props, false)
  );

  const renderDisabledCheckbox = (props: IDetailsCheckboxProps | undefined): JSX.Element => (
    renderCheckbox(props, true)
  );

  const renderDetailsHeader = (headerProps?: IDetailsHeaderProps): JSX.Element | null => {
    if (!headerProps) {
      return null;
    }
    return <DetailsHeader {...headerProps} onRenderDetailsCheckbox={renderHeaderCheckbox} styles={headerStyles} />;
  };

  const onHeaderCheckboxChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newChecked?: boolean): void => {
    if (isHeaderCbIndeterminate) {
      setIsHeaderCbIndeterminate(false);
    } else {
      setIsHeaderCbChecked(!newChecked);
      selectionState.setAllSelected(!newChecked);
    }
  };

  const renderHeaderCheckbox = (): JSX.Element => (
    <Checkbox
      indeterminate={isHeaderCbIndeterminate}
      checked={isHeaderCbChecked}
      onChange={onHeaderCheckboxChange}
      styles={headerCheckboxStyle}
    />
  );

  const renderRow = (props: IDetailsRowProps | undefined): JSX.Element | null => {
    if (props) {
      if (isRowSelectionDisabled && isRowSelectionDisabled(props.item)) {
        return (
          <span data-selection-disabled>
            <DetailsRow
              data-test-id={`${buildListingTestIds(testId).row}`}
              rowFieldsAs={renderRowFields}
              {...props}
              styles={tableRowStyles}
              onRenderDetailsCheckbox={renderDisabledCheckbox}
            />
          </span>
        );
      }
      return (
        <DetailsRow
          data-test-id={`${buildListingTestIds(testId).row}`}
          rowFieldsAs={renderRowFields}
          {...props}
          styles={tableRowStyles}
        />
      );
    }
    return null;
  };

  const renderViewTitle = (options?: ViewSelectOption []): JSX.Element => {
    const option = options && options[0];
    return renderViewOption(option);
  };

  const renderViewOption = (option?: ViewSelectOption): JSX.Element => (
    <div>
      <span style={{ position: "relative", top: "3px" }}>{option?.icon && <Icon iconName={option.icon} />}</span>
      <span style={{ paddingLeft: "5px" }}>{option?.text}</span>
    </div>
  );

  return (
    <Stack
      data-test-id={buildListingTestIds(testId).component}
      className={LISTING_CLASS_NAME}
      verticalFill
    >
      {actionBarItems && (
        <ActionBar
          actions={actionBarItems}
          infoBlocks={infoBlocks}
          disableAll={disableAllActions}
          onActionClick={onActionClick}
        />
      )}
      {filtering && filtering.defaultExtraFilters
        && (
          <div
            style={{
              paddingTop: "10px",
              paddingBottom: "5px",
            }}
          >
            <Filters
              items={items}
              columns={listColumns}
              setFilteredItems={setFilteredItems}
              allExtraFilters={filtering.allExtraFilters}
              defaultExtraFilters={filtering.defaultExtraFilters}
              key={filterKey}
              hideTextFilter={filtering.hideTextFilter}
              hideAddFilterPill={filtering.hideAddFilterPill}
              filterLayout={filtering.filterLayout}
              textFilterCallBack={filtering.textFilterCallBack}
              componentRef={filtering.componentRef}
              defaultFilterText={filtering.defaultFilterText}
            />
          </div>
        )}
      { (groupingSelect || viewSelect)
        && (
        <Stack
          horizontal
          horizontalAlign="end"
          tokens={viewGroupingStackTokens}
          style={{ padding: "10px 0px" }}
        >
          {groupingSelect && (
          <Dropdown
            styles={groupDropdownStyles}
            options={groupingSelect.groupingOptions}
            defaultSelectedKey={groupingSelect.defaultSelectedKey}
            onChange={groupingSelect.onGroupingChange}
          />
          )}
          {viewSelect && (
          <Dropdown
            styles={viewDropdownStyles}
            options={viewSelect.viewOptions}
            defaultSelectedKey={viewSelect.defaultSelectedKey}
            onChange={viewSelect.onViewChange}
            onRenderOption={renderViewOption}
            onRenderTitle={renderViewTitle}
          />
          )}
        </Stack>
        )}
      <Stack style={{ position: "relative" }}>
        {isLoading && (
        <>
          <ProgressDotsIndicator testId={buildListingTestIds(testId).loadingDots} />
          <Overlay style={{ zIndex: 1 }} />
        </>
        )}
        <DetailsList
          items={itemsToShow}
          columns={columns}
          setKey={itemKey}
          layoutMode={DetailsListLayoutMode.fixedColumns}
          onShouldVirtualize={() => false}
          constrainMode={ConstrainMode.unconstrained}
          selection={selectionState}
          selectionPreservedOnEmptyClick
          selectionMode={getSelectionMode()}
          styles={gridStyles}
          checkboxVisibility={CheckboxVisibility.always}
          onRenderRow={renderRow}
          onRenderCheckbox={renderEnabledCheckbox}
          onRenderItemColumn={renderCustomColumn}
          onColumnHeaderClick={headerColumnClick}
          onRenderDetailsHeader={renderDetailsHeader}
        />
        {showWatermark && emptyList && !isLoading ? (
          emptyList.watermark
          || emptyList.description
          || emptyList.emptyButtons
          || emptyList.learnMoreLink
          || emptyList.clearFilters
        ) ? (
          <ListingEmptyWatermark
            testId={testId}
            watermark={emptyList.watermark}
            title={emptyList.title}
            description={emptyList.description}
            emptyButtons={emptyList.emptyButtons}
            learnMoreLink={emptyList.learnMoreLink}
            clearFilters={() => {
              if (filtering?.onClearFilters) filtering.onClearFilters();
              setFilterKey(uniqueGUID());
            }}
          />
          ) : <Label>{emptyList.title}</Label> : <div />}
      </Stack>
      {!!itemsToShow.length && itemsPerPage && (
        <Stack style={{ position: "relative" }}>
          <ListingPagination
            showing={{
              fromItems,
              toItems,
              totalItems,
            }}
            currentPage={currentPage}
            pageCount={pageCount}
            onNext={onNext}
            onPrevious={onPrevious}
            onPageSelect={onPageSelect}
          />
        </Stack>
      )}
    </Stack>
  );
}
